import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { FiPlus, FiEdit2, FiTrash2, FiEye, FiSearch, FiFilter, FiCalendar, FiMapPin, FiBriefcase, FiDollarSign, FiBold, FiItalic, FiList, FiLink, FiUsers, FiTrendingUp, FiActivity, FiRefreshCw, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import api from '../../utils/api';
import { TbCurrencyRupee } from 'react-icons/tb';

const JobManagement = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [stats, setStats] = useState({});
  const editorRef = useRef(null);

  // Text editor functions
  const formatText = (command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
  };

  const insertList = (type) => {
    const listHTML = type === 'ul' 
      ? '<ul><li>List item</li></ul>' 
      : '<ol><li>List item</li></ol>';
    document.execCommand('insertHTML', false, listHTML);
    editorRef.current?.focus();
  };

  const insertLink = () => {
    const url = prompt('Enter URL:');
    if (url) {
      document.execCommand('createLink', false, url);
      editorRef.current?.focus();
    }
  };

  const handleEditorChange = () => {
    const content = editorRef.current?.innerHTML || '';
    setFormData(prev => ({ ...prev, description: content }));
  };

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    location: '',
    position: '',
    salaryRange: {
      min: 0,
      max: 0
    },
    requiredSkills: [],
    experience: '',
    numberOfVacancies: 1,
    isActive: true
  });

  const locations = [
    { id: '691255944d172c62233116fc', name: 'Location 1' },
    { id: '691255944d172c62233116fd', name: 'Location 2' }
  ];

  useEffect(() => {
    fetchJobs();
    fetchStats();
  }, [currentPage, searchTerm, filterStatus]);

  const fetchJobs = async (isManualRefresh = false) => {
    try {
      if (isManualRefresh) {
        setRefreshing(true);
        toast.loading('Refreshing jobs...', { id: 'refresh' });
      } else {
        setLoading(true);
      }

      const params = new URLSearchParams({
        page: currentPage,
        limit: 10,
        ...(searchTerm && { search: searchTerm }),
        ...(filterStatus !== 'all' && { isActive: filterStatus === 'active' })
      });

      console.log('Fetching jobs with params:', params);
      const response = await api.get(`/jobs?${params}`);
      console.log('Jobs response:', response.data);
      
      if (response.data.success) {
        setJobs(response.data.data);
        setTotalPages(response.data.pages);
        
        // Show success message for manual refresh
        if (isManualRefresh) {
          toast.success(`Refreshed! Found ${response.data.data.length} jobs`, { id: 'refresh' });
        }
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error('Fetch jobs error:', error);
      toast.error(`Failed to fetch jobs: ${error.response?.data?.message || error.message}`, { id: 'refresh' });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/jobs/stats');
      
      if (response.data.success) {
        setStats(response.data.data.overview);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      let response;
      if (editingJob) {
        response = await api.put(`/jobs/${editingJob._id}`, formData);
      } else {
        response = await api.post('/jobs', formData);
      }
      
      if (response.data.success) {
        toast.success(response.data.message);
        setShowModal(false);
        setEditingJob(null);
        resetForm();
        fetchJobs();
        fetchStats();
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error('Failed to save job');
    }
  };

  const handleEdit = (job) => {
    setEditingJob(job);
    setFormData({
      title: job.title,
      description: job.description,
      location: job.location || '',
      position: job.position || '',
      salaryRange: job.salaryRange || {
        min: 0,
        max: 0
      },
      requiredSkills: job.requiredSkills || [],
      experience: job.experience || '',
      numberOfVacancies: job.numberOfVacancies || 1,
      isActive: job.isActive !== undefined ? job.isActive : true
    });
    setShowModal(true);
    
    // Set editor content after modal opens
    setTimeout(() => {
      if (editorRef.current && job.description) {
        editorRef.current.innerHTML = job.description;
      }
    }, 100);
  };

  const handleDelete = async (jobId) => {
    if (!window.confirm('Are you sure you want to delete this job?')) return;

    try {
      const response = await api.delete(`/jobs/${jobId}`);
      
      if (response.data.success) {
        toast.success(response.data.message);
        fetchJobs();
        fetchStats();
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error('Failed to delete job');
    }
  };

  const toggleJobStatus = async (jobId) => {
    try {
      const response = await api.patch(`/jobs/${jobId}/toggle`);
      
      if (response.data.success) {
        toast.success(response.data.message);
        fetchJobs();
        fetchStats();
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error('Failed to toggle job status');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      location: '',
      position: '',
      salaryRange: {
        min: 0,
        max: 0
      },
      requiredSkills: [],
      experience: '',
      numberOfVacancies: 1,
      isActive: true
    });
    
    // Clear editor content
    if (editorRef.current) {
      editorRef.current.innerHTML = '';
    }
  };

  const handleArrayInput = (field, value) => {
    const items = value.split(',').map(item => item.trim()).filter(item => item);
    setFormData(prev => ({ ...prev, [field]: items }));
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatSalary = (salaryRange) => {
    if (!salaryRange) return 'Not specified';
    const { min, max } = salaryRange;
    return `${min.toLocaleString()} - ${max.toLocaleString()}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-600 font-medium">Loading jobs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg flex items-center justify-center">
                  <FiBriefcase className="text-white text-xl" />
                </div>
                Job Management
              </h1>
              <p className="text-gray-600 mt-2">Manage job postings and applications</p>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-purple-100 text-purple-800 rounded-full">
              <FiBriefcase className="text-purple-600" />
              <span className="font-semibold">{stats.totalJobs || 0} Total Jobs</span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-md border border-gray-100 p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-medium text-gray-600">Total Jobs</h3>
                <div className="text-2xl font-bold text-gray-900">{stats.totalJobs || 0}</div>
                <p className="text-xs text-gray-500">All job postings</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <FiBriefcase className="text-blue-600 text-xl" />
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-md border border-gray-100 p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-medium text-gray-600">Active Jobs</h3>
                <div className="text-2xl font-bold text-green-600">{stats.activeJobs || 0}</div>
                <p className="text-xs text-gray-500">Currently open</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <FiActivity className="text-green-600 text-xl" />
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filter Bar */}
        <div className="bg-white rounded-xl shadow-md border border-gray-100 p-6 mb-8">
          <div className="flex flex-wrap gap-4 items-center">
            {/* Search */}
            <div className="flex-1 min-w-[300px] relative">
              <FiSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search jobs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200"
              />
            </div>
            
            {/* Filter */}
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200"
            >
              <option value="all">All Jobs</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>

            {/* Refresh Button */}
            <button
              onClick={() => fetchJobs(true)}
              disabled={refreshing}
              className="px-4 py-3 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-lg hover:from-purple-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-md hover:shadow-lg flex items-center gap-2"
            >
              <FiRefreshCw className={`text-sm ${refreshing ? 'animate-spin' : ''}`} />
              <span className="text-sm font-medium">{refreshing ? 'Refreshing...' : 'Refresh'}</span>
            </button>

            {/* Create Job Button */}
            <button
              onClick={() => {
                resetForm();
                setEditingJob(null);
                setShowModal(true);
              }}
              className="group relative px-4 py-2.5 bg-gradient-to-r from-purple-500 to-pink-600 text-white rounded-lg font-medium hover:from-purple-600 hover:to-pink-700 transition-all duration-200 shadow-md hover:shadow-lg flex items-center gap-2"
            >
              <FiPlus className="text-sm group-hover:rotate-90 transition-transform duration-200" />
              <span className="text-sm font-medium">Create Job</span>
              <div className="absolute -top-1 -right-1 w-2 h-2 bg-purple-400 rounded-full animate-pulse"></div>
            </button>
          </div>
        </div>

        {/* Jobs Table */}
        <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-purple-50 to-pink-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Title</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Position</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Location</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Salary Range</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Vacancies</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {jobs.map((job) => (
                  <tr key={job._id} className="hover:bg-gray-50 transition-colors duration-150">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{job.title}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-600">{job.position}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-600">{job.location}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 font-medium">
                        {formatSalary(job.salaryRange)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-600">{job.numberOfVacancies}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                        job.isActive 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {job.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <button 
                          onClick={() => handleEdit(job)}
                          className="group relative p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-all duration-200 hover:scale-110"
                          title="Edit Job"
                        >
                          <FiEdit2 className="w-4 h-4 group-hover:rotate-12 transition-transform" />
                          <div className="absolute -top-1 -right-1 w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
                        </button>
                        <button 
                          onClick={() => handleDelete(job._id)}
                          className="group relative p-2 text-red-600 hover:bg-red-100 rounded-lg transition-all duration-200 hover:scale-110"
                          title="Delete Job"
                        >
                          <FiTrash2 className="w-4 h-4 group-hover:scale-110 transition-transform" />
                          <div className="absolute -top-1 -right-1 w-2 h-2 bg-red-400 rounded-full animate-pulse"></div>
                        </button>
                        <button 
                          onClick={() => toggleJobStatus(job._id)}
                          className={`group relative p-2 rounded-lg transition-all duration-200 hover:scale-110 ${
                            job.isActive 
                              ? 'text-amber-600 hover:bg-amber-100' 
                              : 'text-green-600 hover:bg-green-100'
                          }`}
                          title={job.isActive ? 'Deactivate Job' : 'Activate Job'}
                        >
                          {job.isActive ? (
                            <FiActivity className="w-4 h-4 group-hover:scale-110 transition-transform" />
                          ) : (
                            <FiActivity className="w-4 h-4 group-hover:scale-110 transition-transform" />
                          )}
                          <div className={`absolute -top-1 -right-1 w-2 h-2 rounded-full animate-pulse ${
                            job.isActive ? 'bg-amber-400' : 'bg-green-400'
                          }`}></div>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {jobs.length === 0 && (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FiBriefcase className="text-gray-400 text-2xl" />
              </div>
              <h3 className="text-lg font-semibold text-gray-700 mb-2">No jobs found</h3>
              <p className="text-gray-500">Create your first job posting to get started</p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white rounded-xl shadow-md border border-gray-100 p-4 mt-6">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-sm text-gray-600">
                Showing {jobs.length} of {stats.totalJobs || 0} jobs
              </div>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-2 bg-gradient-to-r from-gray-500 to-gray-600 text-white rounded-lg hover:from-gray-600 hover:to-gray-700 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed transition-all duration-200 shadow-sm hover:shadow-md flex items-center gap-2"
                >
                  <FiChevronLeft className="w-4 h-4" />
                  Previous
                </button>
                
                {[...Array(totalPages)].map((_, idx) => {
                  const pageNum = idx + 1;
                  if (
                    pageNum === 1 ||
                    pageNum === totalPages ||
                    (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                  ) {
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`px-3 py-2 rounded-lg font-medium transition-all duration-200 ${
                          pageNum === currentPage
                            ? 'bg-gradient-to-r from-purple-500 to-purple-600 text-white shadow-md'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  } else if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
                    return <span key={pageNum} className="px-2 text-gray-400">...</span>;
                  }
                  return null;
                })}
                
                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-2 bg-gradient-to-r from-gray-500 to-gray-600 text-white rounded-lg hover:from-gray-600 hover:to-gray-700 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed transition-all duration-200 shadow-sm hover:shadow-md flex items-center gap-2"
                >
                  Next
                  <FiChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Job Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-4 flex-shrink-0">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-white/20 backdrop-blur-md rounded-xl flex items-center justify-center">
                      <FiBriefcase className="text-white text-xl" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-white">
                        {editingJob ? 'Edit Job' : 'Create Job'}
                      </h2>
                      <p className="text-purple-100 text-sm mt-0.5">Fill in the job details below</p>
                    </div>
                  </div>
                  <button 
                    onClick={() => {
                      setShowModal(false);
                      setEditingJob(null);
                      resetForm();
                    }}
                    className="w-9 h-9 bg-white/20 hover:bg-white/30 backdrop-blur-md rounded-lg flex items-center justify-center text-white hover:rotate-90 transition-all duration-300"
                  >
                    <span className="text-xl">×</span>
                  </button>
                </div>
              </div>

              {/* Modal Body */}
              <div className="p-6 overflow-y-auto flex-1 bg-gray-50">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Job Title */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Job Title <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200"
                        placeholder="Enter job title"
                        required
                      />
                    </div>

                    {/* Position */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Position <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.position}
                        onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200"
                        placeholder="Enter position"
                        required
                      />
                    </div>

                    {/* Location */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Location <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.location}
                        onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200"
                        placeholder="Enter location"
                        required
                      />
                    </div>

                    {/* Experience */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Experience Required
                      </label>
                      <input
                        type="text"
                        value={formData.experience}
                        onChange={(e) => setFormData({ ...formData, experience: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200"
                        placeholder="e.g., 2-3 years"
                      />
                    </div>

                    {/* Salary Range */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Salary Range
                      </label>
                      <div className="flex items-center gap-2">
                        <div className="flex-1">
                          <input
                            type="number"
                            value={formData.salaryRange.min}
                            onChange={(e) => setFormData({ 
                              ...formData, 
                              salaryRange: { ...formData.salaryRange, min: parseInt(e.target.value) || 0 }
                            })}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200"
                            placeholder="Min"
                          />
                        </div>
                        <span className="text-gray-500">-</span>
                        <div className="flex-1">
                          <input
                            type="number"
                            value={formData.salaryRange.max}
                            onChange={(e) => setFormData({ 
                              ...formData, 
                              salaryRange: { ...formData.salaryRange, max: parseInt(e.target.value) || 0 }
                            })}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200"
                            placeholder="Max"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Number of Vacancies */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Number of Vacancies
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={formData.numberOfVacancies}
                        onChange={(e) => setFormData({ ...formData, numberOfVacancies: parseInt(e.target.value) || 1 })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200"
                      />
                    </div>
                  </div>

                  {/* Required Skills */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Required Skills
                    </label>
                    <input
                      type="text"
                      value={formData.requiredSkills.join(', ')}
                      onChange={(e) => handleArrayInput('requiredSkills', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200"
                      placeholder="Enter skills separated by commas"
                    />
                    <p className="text-xs text-gray-500 mt-1">Separate multiple skills with commas</p>
                  </div>

                  {/* Job Description */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Job Description <span className="text-red-500">*</span>
                    </label>
                    <div className="border border-gray-300 rounded-lg overflow-hidden">
                      {/* Editor Toolbar */}
                      <div className="bg-gray-50 border-b border-gray-300 p-2 flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => formatText('bold')}
                          className="p-2 hover:bg-gray-200 rounded transition-colors"
                          title="Bold"
                        >
                          <FiBold className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => formatText('italic')}
                          className="p-2 hover:bg-gray-200 rounded transition-colors"
                          title="Italic"
                        >
                          <FiItalic className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => insertList('ul')}
                          className="p-2 hover:bg-gray-200 rounded transition-colors"
                          title="Bullet List"
                        >
                          <FiList className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={insertLink}
                          className="p-2 hover:bg-gray-200 rounded transition-colors"
                          title="Insert Link"
                        >
                          <FiLink className="w-4 h-4" />
                        </button>
                      </div>
                      
                      {/* Editor Content */}
                      <div
                        ref={editorRef}
                        contentEditable
                        onInput={handleEditorChange}
                        className="min-h-[200px] p-4 bg-white focus:outline-none"
                        style={{ minHeight: '200px' }}
                      />
                    </div>
                  </div>

                  {/* Status Toggle */}
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-gray-700">
                      Job Status
                    </label>
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, isActive: !formData.isActive })}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        formData.isActive ? 'bg-purple-600' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          formData.isActive ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                    <span className="text-sm text-gray-600">
                      {formData.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </form>
              </div>

              {/* Modal Footer */}
              <div className="px-6 py-4 bg-white border-t border-gray-200 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingJob(null);
                    resetForm();
                  }}
                  className="px-6 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-all duration-200 font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  onClick={handleSubmit}
                  className="px-6 py-2.5 bg-gradient-to-r from-purple-500 to-pink-600 text-white rounded-lg hover:from-purple-600 hover:to-pink-700 transition-all duration-200 shadow-md hover:shadow-lg font-medium"
                >
                  {editingJob ? 'Update Job' : 'Create Job'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default JobManagement;
